import React, { useState, useEffect } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Shield, ExternalLink, Search, Menu, X } from "lucide-react";
import { useAuth } from "../AuthContext";
import { cn } from "../lib/utils";
import { ReactComponent as Logo } from "../images/compliance-kompas.svg";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, role, signOut } = useAuth();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [isOpen, setIsOpen] = useState(false);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const urlSearch = searchParams.get("search");
    if (urlSearch !== searchTerm) {
      setSearchTerm(urlSearch || "");
    }
  }, [searchParams]);

  // Close mobile menu when location changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const linkClass = (path) =>
    cn(
      "px-4 py-2 rounded-md transition-colors text-sm font-medium",
      isActive(path)
        ? "bg-nn-50 text-nn-500"
        : "text-nn-gray-600 hover:text-nn-500 hover:bg-nn-50"
    );

  const isHomePage = location.pathname === "/";

  const mobileMenuClass = cn(
    "fixed inset-0 z-50 transform transition-transform duration-300 lg:hidden",
    isOpen ? "translate-x-0" : "translate-x-full"
  );

  return (
    <nav className="py-4 lg:py-6 bg-gray-50">
      <div className="container p-0">
        <div className="flex justify-between items-center">
          {/* Left section with logo */}
          <div className="flex items-center mr-12">
            <Link to="/">
              <Logo />
            </Link>
          </div>

          {/* Desktop Navigation - hidden on mobile */}
          <div className="hidden lg:flex items-center gap-8 flex-1">
            {/* Search bar - hidden on home */}
            {!isHomePage && (
              <div className="flex-1 max-w-lg">
                <form onSubmit={handleSearch}>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Zoek..."
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md
                        placeholder:text-gray-500 text-gray-900 text-sm
                        bg-white shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-nn-500 focus:border-nn-500
                        transition-colors"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2
                        p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Search className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Desktop Actions - hidden on mobile */}
          <div className="hidden lg:flex items-center divide-x divide-gray-200">
            <div className="pr-4">
              <a
                href="https://notebooklm.google.com/notebook/6f584665-0acb-4850-b6d3-f5cc5c5f3cbf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-md transition-colors text-sm font-medium bg-nn-teal-500 text-white hover:bg-nn-teal-600"
              >
                <span>Chatbot</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 lg:hidden text-gray-600 hover:text-gray-900"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={mobileMenuClass}>
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed inset-y-0 right-0 w-64 bg-white shadow-xl">
            <div className="flex flex-col h-full">
              {/* Mobile Search - hidden on home */}
              {!isHomePage && (
                <div className="p-4">
                  <form onSubmit={handleSearch}>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Zoek..."
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md
                          placeholder:text-gray-500 text-gray-900 text-sm
                          bg-white shadow-sm
                          focus:outline-none focus:ring-2 focus:ring-nn-500 focus:border-nn-500
                          transition-colors"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <button
                        type="submit"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2
                          p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <Search className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Mobile Navigation Links */}
              <div className="flex flex-col p-4 space-y-2">
                <Link to="/" className={linkClass("/")}>
                  Home
                </Link>
                <a
                  href="https://notebooklm.google.com/notebook/cd08f65c-2e8b-4b94-adc5-9a3c80875daa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-md transition-colors text-sm font-medium bg-nn-teal-500 text-white hover:bg-nn-teal-600"
                >
                  <span>Chatbot</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
